// CRM Contacts List — search, filter, create contacts
import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { Plus, Search, Users } from 'lucide-react'
import { getCrmContacts, createCrmContact, deleteCrmContact } from '../../../services/api'
import type { CrmContact } from '../../../types'
import ContactForm from '../../../components/crm/ContactForm'
import PageHeader from '../../../components/ui/PageHeader'
import EmptyState from '../../../components/ui/EmptyState'
import ConfirmDialog from '../../../components/ui/ConfirmDialog'

export default function ContactsList() {
  const { projectId } = useParams<{ projectId: string }>()
  const [contacts, setContacts] = useState<CrmContact[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [showForm, setShowForm] = useState(false)
  const [error, setError] = useState('')
  const [pendingDelete, setPendingDelete] = useState<CrmContact | null>(null)

  const loadContacts = () => {
    if (!projectId) return
    setLoading(true)
    getCrmContacts(projectId)
      .then((data) => setContacts(data.contacts || []))
      .catch(() => setContacts([]))
      .finally(() => setLoading(false))
  }

  useEffect(() => { loadContacts() }, [projectId])

  const filtered = contacts.filter((c) => {
    const q = search.toLowerCase()
    return !q || `${c.first_name} ${c.last_name} ${c.email} ${c.organisation_name}`.toLowerCase().includes(q)
  })

  const handleCreate = async (data: Partial<CrmContact>) => {
    if (!projectId) return
    setError('')
    try {
      await createCrmContact({ ...data, project_id: projectId })
      setShowForm(false)
      loadContacts()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create contact')
    }
  }

  // Honest delete (P7): the ConfirmDialog runs the real mutation and only
  // closes on a genuine success. A thrown error keeps the dialog open with
  // the server's message — no fake success.
  const confirmDelete = async () => {
    if (!projectId || !pendingDelete) return
    await deleteCrmContact(pendingDelete.contact_id, projectId)
    loadContacts()
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Contacts"
        description="The people you're tracking in this project — search, add, and open a contact to log interactions."
        helpKey="crm"
        status={{ label: `(${filtered.length})` }}
        primaryAction={
          <button onClick={() => setShowForm(true)} className="flex items-center gap-2 px-3 py-2 bg-sky-500 text-white text-sm font-medium rounded-lg hover:bg-sky-600">
            <Plus size={16} /> New Contact
          </button>
        }
      />

      {error && (
        <div className="flex items-center justify-between gap-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
          <span>{error}</span>
          <button onClick={() => setError('')} className="text-xs text-red-400 hover:text-red-600 shrink-0">Dismiss</button>
        </div>
      )}

      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
        <input type="text" placeholder="Search contacts..." value={search} onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500" />
      </div>

      {loading ? (
        <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-16 bg-sky-500/10 rounded-lg animate-pulse" />)}</div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={Users}
          title={search ? 'No contacts match your search' : 'No contacts yet'}
          description={search
            ? 'Try a different name, email, or organisation.'
            : 'Add your first contact to start tracking the people in this project.'}
          action={search ? undefined : (
            <button onClick={() => setShowForm(true)} className="inline-flex items-center gap-2 px-4 py-2 bg-sky-500 text-white text-sm font-medium rounded-lg hover:bg-sky-600">
              <Plus size={16} /> Add a contact
            </button>
          )}
        />
      ) : (
        <div className="light-card divide-y divide-sky-200/30">
          {filtered.map((c) => (
            <div key={c.contact_id} className="flex items-center gap-3 px-4 py-3 hover:bg-white/30">
              <Link to={`/project/${projectId}/crm/contacts/${c.contact_id}`} className="flex-1 flex items-center gap-3">
                <div className="w-9 h-9 bg-sky-500/10 rounded-full flex items-center justify-center text-xs font-semibold text-sky-700">
                  {c.first_name?.[0]}{c.last_name?.[0]}
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">{c.first_name} {c.last_name}</p>
                  {/* LIVE-DATA fix: never print "No details" — omit the
                      sub-line gracefully when there's genuinely nothing to show. */}
                  {(c.email || c.organisation_name) && (
                    <p className="text-xs text-gray-500">{c.email || c.organisation_name}</p>
                  )}
                </div>
                {c.source === 'author-conversion' && (
                  <span className="text-xs bg-amber-500/20 text-amber-700 px-2 py-0.5 rounded-full">Author</span>
                )}
              </Link>
              <button onClick={() => setPendingDelete(c)} className="text-xs text-red-400 hover:text-red-600">Delete</button>
            </div>
          ))}
        </div>
      )}

      {showForm && <ContactForm onSubmit={handleCreate} onClose={() => setShowForm(false)} />}

      <ConfirmDialog
        open={!!pendingDelete}
        tone="danger"
        title="Delete contact?"
        message={
          pendingDelete
            ? `This permanently removes ${pendingDelete.first_name} ${pendingDelete.last_name} and their interactions from this project. This can't be undone.`
            : ''
        }
        confirmLabel="Delete contact"
        onConfirm={confirmDelete}
        onClose={() => setPendingDelete(null)}
      />
    </div>
  )
}
